import { C1_DOOR_TYPE_UI_LABELS, C1_GARAGE_DOOR_TYPE_UI_LABELS, C1_WINDOW_SHADING_LABELS } from "../C1_uiLabels";
import { C1_BaseObject } from "./C1_BaseObject";
import type { C1_OpeningData, DoorType, GarageDoorType, WallSide, WindowShading } from "../types/C1_ModelTypes";
import type { C1_EloItem_Data } from "../types/C1_EloTypes";
import type { C1_Project } from "./C1_Project";
import type { C1_Room } from "./C1_Room";
import type Room from "../../../src/models/Room";
import { c1_state } from "./C1_runtimeContext";
import BaseModel from "../../../src/models/BaseModel";
import ObjectFactory from "../../../src/models/ObjectFactory";
import { getOpeningDefinition, type C1_OpeningDefinition } from "../data/C1_openingDefinitions";
import { getMaterializedEloRoot, replaceMaterializedEloDefinitionTree } from "./C1_materializeEloDefinition";
import { getOpeningEloProfile } from "../data/C1_OpeningEloDefinitions";
import type Group from "../../../src/models/Group";
import type { C1_Wall } from "./C1_Wall";
import { roomWallThickness } from "./C1_wallThickness";
import { isSpecialExteriorRoomType } from "../types/C1_RoomTypes";


const WINDOW_WALL_DIRECTIONS: Record<WallSide, number> = {
  top: 0,
  right: 90,
  bottom: 180,
  left: 270,
};

export function windowDirection(side: WallSide, northAngle: number): number {
  const direction = (WINDOW_WALL_DIRECTIONS[side] - northAngle + 360) % 360;
  return Math.round(direction * 10) / 10;
}

export class C1_Opening extends C1_BaseObject<C1_OpeningData> {
  private _temporaryRoom?: C1_Room;

  get className(): string {
    return "C1_Opening";
  }

  get room(): C1_Room | undefined {
    if (!this.owner) return this._temporaryRoom;
    return (this.owner.parent as Room | undefined)?.c1 as C1_Room | undefined;
  }

  /** Room used by a transient opening before an ESTADA opening owns it. */
  get temporaryRoom(): C1_Room | undefined {
    return this._temporaryRoom;
  }

  set temporaryRoom(room: C1_Room | undefined) {
    this._temporaryRoom = room;
  }

  constructor(data: C1_OpeningData, owner?: BaseModel) {
    const doorType: DoorType = data.doorType && data.doorType in C1_DOOR_TYPE_UI_LABELS ? data.doorType : "entrance";
    const garageDoorType: GarageDoorType =
      data.garageDoorType && data.garageDoorType in C1_GARAGE_DOOR_TYPE_UI_LABELS
        ? data.garageDoorType
        : "sectional";
    const windowShading: WindowShading =
      data.windowShading === "blind" || data.windowShading === "jalousie" || data.windowShading === "none"
        ? data.windowShading
        : "none";
    super(
      {
        ...data,
        doorType: data.kind === "door" ? doorType : undefined,
        garageDoorType: data.kind === "garage-door" ? garageDoorType : undefined,
        windowShading: data.kind === "window" || doorType === "garden" ? windowShading : undefined,
      },
      owner,
    );
    if (!this._data._wallId) this._data._wallId = this.room?.wallForSide(data.side)?.id;
  }

  get kind() {
    return this.data.kind;
  }

  /** Visual object definition shared by the building palette and navigator. */
  get definition(): C1_OpeningDefinition {
    return getOpeningDefinition(this.kind, this.doorType);
  }

  get displayTypeName(): string {
    return this.getDisplayTypeName();
  }

  getDisplayTypeName(): string {
    if (this.kind === "window") {
      return C1_WINDOW_SHADING_LABELS[this.windowShading];
    }
    if (this.kind === "garage-door") return C1_GARAGE_DOOR_TYPE_UI_LABELS[this.garageDoorType];
    return C1_DOOR_TYPE_UI_LABELS[this.doorType];
  }

  get mode() {
    return this.data.mode;
  }

  get roomId() {
    return this.room?.id ?? "";
  }

  get horizontal() {
    const points = this.wall ? this.room?.wallPoints(this.wall) : undefined;
    if (points) return Math.abs(points.end.x - points.start.x) >= Math.abs(points.end.y - points.start.y);
    return this.side === "top" || this.side === "bottom";
  }

  /** Wall containing this opening; only `_wallId` is persisted. */
  get wall(): C1_Wall | undefined {
    return state.objectRegistry.getObject(this.data._wallId ?? "") as C1_Wall | undefined;
  }

  set wall(wall: C1_Wall | undefined) {
    const wallId = wall?.id ?? "";
    if (wallId === (this.data._wallId ?? "")) return;
    this.beforeChange("wallId");
    this._data._wallId = wallId;
    this.changed("wallId");
  }

  get absolutePosition(): { x: number; y: number } {
    return this.openingCenter();
  }

  /** Calculates the opening center in floor coordinates from its room wall and offset. */
  openingCenter(): { x: number; y: number } {
    const room = this.room;
    if (!room) throw new Error(`C1 opening ${this.id} has no owning room.`);

    const offset = Math.min(Math.max(this.offset ?? 0.5, 0), 1);
    const wallPoints = this.wall ? room.wallPoints(this.wall) : undefined;
    if (wallPoints) {
      return {
        x: wallPoints.start.x + (wallPoints.end.x - wallPoints.start.x) * offset,
        y: wallPoints.start.y + (wallPoints.end.y - wallPoints.start.y) * offset,
      };
    }
    const rect = room.rect();
    if (this.side === "left") {
      return { x: rect.x1, y: rect.y1 + (rect.y2 - rect.y1) * offset };
    }
    if (this.side === "right") {
      return { x: rect.x2, y: rect.y1 + (rect.y2 - rect.y1) * offset };
    }
    if (this.side === "top") {
      return { x: rect.x1 + (rect.x2 - rect.x1) * offset, y: rect.y1 };
    }
    return { x: rect.x1 + (rect.x2 - rect.x1) * offset, y: rect.y2 };
  }

  /**
   * The room on the opposite side of an interior door.
   * Exterior doors and windows do not have a partner room.
   */
  get partnerRoom(): C1_Room | undefined {
    if (this.kind !== "door" || (this.doorType !== "interior" && this.doorType !== "passage")) return undefined;
    const partnerRoom = this.wall?.partnerWall?.parent?.c1;
    return partnerRoom?.className === "C1_Room" ? (partnerRoom as C1_Room) : undefined;
  }

  get partnerRoomName(): string | undefined {
    return (this.partnerRoom?.owner?.name ?? "");
  }

  get side() {
    return this.data.side;
  }

  /** Exterior orientation, independent of which side of a shared wall owns the window. */
  get outwardSide(): WallSide {
    const room = this.room;
    if (this.kind === "window" && room && isSpecialExteriorRoomType(room.type)) {
      const partnerWall = this.wall?.partnerWall;
      const partnerRoom = partnerWall?.parent?.c1 as C1_Room | undefined;
      if (partnerWall && partnerRoom?.className === "C1_Room" && !isSpecialExteriorRoomType(partnerRoom.type)) {
        return partnerRoom.compatibilitySide(partnerWall);
      }
    }
    return this.side;
  }

  get width() {
    return this.data.width;
  }

  get offset() {
    return this.data.offset;
  }

  get reflect() {
    return this.data.reflect;
  }

  get eloTree(): C1_EloItem_Data | undefined {
    const rootDefinitionId = getOpeningEloProfile(this).rootDefinitionId;
    return getMaterializedEloRoot(this.owner as Group | undefined, rootDefinitionId)?.toLegacyTreeData();
  }

  get windowShading(): WindowShading {
    return this.data.windowShading ?? "jalousie";
  }

  get doorType(): DoorType {
    return this.data.doorType ?? "entrance";
  }

  get garageDoorType(): GarageDoorType {
    return this.data.garageDoorType ?? "sectional";
  }

  get slidingDoor(): boolean {
    return this.kind === "door" && this.data.slidingDoor === true;
  }

  get doorLeafCount(): number {
    if (this.kind !== "door") return 1;
    const count = Math.max(1, Math.floor(this.data.doorLeafCount ?? 1));
    return this.slidingDoor ? Math.min(count, 2) : count;
  }

  get project(): C1_Project {
    return c1_state.project;
  }

  equipmentPostitionGeometric(eloEquipmentId: string): { x: number; y: number } | undefined {
    const room = this.room;
    if (!room) return undefined;

    const project = this.project;

    const center = this.absolutePosition;

    const wallThickness = this.wall ? roomWallThickness(room, this.wall, project) : project.settings.wallThicknessExterior;
    const distanceFromOpening = 0.1;

    let dw = 0;
    if (eloEquipmentId === "int") {
      dw = -wallThickness / 2;
    } else {
      dw = wallThickness / 2;
    }

    return this.horizontal
      ? {
          x: center.x + this.width / 2 + distanceFromOpening,
          y: center.y + dw,
        }
      : {
          x: center.x + dw,
          y: center.y + this.width / 2 + distanceFromOpening + wallThickness / 2,
        };
  }

  setDoorType(doorType: DoorType) {
    if (this.kind !== "door" || doorType === this.data.doorType) return;
    this.beforeChange("doorType");
    this._data.doorType = doorType;
    this.changed("doorType");
  }

  setSlidingDoor(slidingDoor: boolean) {
    if (this.kind !== "door" || slidingDoor === this.data.slidingDoor) return;
    this.beforeChange("slidingDoor");
    this._data.slidingDoor = slidingDoor;
    if (slidingDoor && (this._data.doorLeafCount ?? 1) > 2) this._data.doorLeafCount = 2;
    this.changed("slidingDoor");
  }

  setDoorLeafCount(doorLeafCount: number) {
    if (this.kind !== "door") return;
    const count = Math.max(1, Math.floor(doorLeafCount) || 1);
    const normalized = this.slidingDoor ? Math.min(count, 2) : count;
    if (normalized === this.data.doorLeafCount) return;
    this.beforeChange("doorLeafCount");
    this._data.doorLeafCount = normalized;
    this.changed("doorLeafCount");
  }

  get direction(): number | undefined {
    if (this.kind !== "window") return undefined;
    return windowDirection(this.outwardSide, this.project.northAngle);
  }

  setWindowShading(windowShading: WindowShading) {
    if (windowShading !== this.data.windowShading) {
      this.beforeChange("windowShading");
      this._data.windowShading = windowShading;
      this.changed("windowShading");
    }
  }

  setEloTree(eloTree?: C1_EloItem_Data) {
    this.beforeChange("eloItems");
    replaceMaterializedEloDefinitionTree(this.owner as Group | undefined, eloTree);
    this.changed("eloItems");
  }

  setWidth(width: number) {
    this.beforeChange("width");
    this._data.width = width;
    this.changed("width");
  }

  setReflect(reflect: boolean) {
    this.beforeChange("reflect");
    this._data.reflect = reflect;
    this.changed("reflect");
  }

  setPlacement(side: WallSide, offset: number, reflect = this.data.reflect) {
    this.beforeChange("placement");
    Object.assign(this._data, {
      side,
      offset,
      reflect,
      mode: "manual" as const,
      _wallId: this.room?.wallForSide(side)?.id,
    });
    this.changed("placement");
  }

  setGarageDoorType(garageDoorType: GarageDoorType) {
    if (this.kind !== "garage-door" || garageDoorType === this.data.garageDoorType) return;
    this.beforeChange("garageDoorType");
    this._data.garageDoorType = garageDoorType;
    this.changed("garageDoorType");
  }

  /** Places the opening directly on a concrete wall without a side-based lookup. */
  setWallPlacement(wall: C1_Wall, offset: number, reflect = this.data.reflect) {
    this.beforeChange("placement");
    Object.assign(this._data, {
      side: this.room?.compatibilitySide(wall) ?? this.side,
      offset: Math.min(Math.max(offset, 0), 1),
      reflect,
      mode: "manual" as const,
      _wallId: wall.id,
    });
    this.changed("placement");
  }

  toJSON(): C1_OpeningData {
    return {
      ...this.data,
      ...(this.kind === "window" ? { direction: this.direction } : {}),
    };
  }
}

ObjectFactory.registerClass("c1:C1_Opening", (data, owner) =>
  new C1_Opening(data, owner) as unknown as BaseModel,
);
